/**
 * AR Effectiveness — collapsible KPI strip rendered above the
 * Outstanding Invoices Kanban. Toggles between 30d / 90d windows,
 * surfaces 4 hero KPIs (reminders sent, pay-rate within 7d,
 * avg days-to-pay, $ recovered), and on demand reveals a top-10
 * "most responsive customers" panel + a CSV export link.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartLineUp, EnvelopeSimple, Lightning, CurrencyDollar, Clock,
  CaretDown, CaretUp, FileCsv, Trophy,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { getArEffectiveness, arEffectivenessCsvUrl } from '../lib/api';

const TONE_DOT = {
  friendly: 'bg-violet-500',
  firm: 'bg-orange-500',
  final: 'bg-rose-600',
};

function formatCurrency(amount, ccy) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'AUD', maximumFractionDigits: 0 })
      .format(amount || 0);
  } catch (_) {
    return `${(amount || 0).toFixed(0)} ${ccy || 'AUD'}`;
  }
}

function pct(v) {
  return `${Math.round((v || 0) * 100)}%`;
}

function Tile({ icon: Icon, label, value, sub, accent = 'violet', testid }) {
  const accentMap = {
    violet: 'border-violet-300 text-violet-700',
    emerald: 'border-emerald-300 text-emerald-700',
    sky: 'border-sky-300 text-sky-700',
    amber: 'border-amber-300 text-amber-700',
  };
  return (
    <div data-testid={testid} className={`bg-white border-2 ${accentMap[accent]} px-4 py-3 flex items-start gap-3`}>
      <Icon size={18} weight="bold" className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="font-bold text-xl tracking-tight truncate">{value}</div>
        {sub && <div className="text-[11px] text-zinc-500 truncate">{sub}</div>}
      </div>
    </div>
  );
}

export default function ArEffectivenessStrip() {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const refresh = async (days) => {
    setLoading(true);
    try {
      const r = await getArEffectiveness(days);
      setData(r.data || null);
    } catch (e) {
      toast.error('Could not load AR effectiveness');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(windowDays); }, [windowDays]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) {
    return <div data-testid="ar-effectiveness-loading" className="text-xs italic text-zinc-400">Loading AR effectiveness…</div>;
  }
  if (!data) return null;

  const sent = data.reminders_sent || {};
  const pr = data.pay_rate_within_7d || {};
  const dtp = data.avg_days_to_pay || {};
  const rec = data.recovered || { amount: 0, currency: 'AUD', count: 0 };
  const top = data.top_responsive_customers || [];

  const overallPay = sent.total
    ? ((pr.friendly * (sent.friendly || 0)) + (pr.firm * (sent.firm || 0)) + (pr.final * (sent.final || 0))) / sent.total
    : 0;

  return (
    <div data-testid="ar-effectiveness-strip" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChartLineUp size={18} weight="bold" className="text-violet-600" />
          <h2 className="text-sm font-bold uppercase tracking-widest">AR effectiveness</h2>
          <span className="text-[11px] text-zinc-400">window:</span>
          {[30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              data-testid={`ar-window-${d}`}
              className={`px-2 py-0.5 text-[11px] font-bold uppercase border ${
                windowDays === d
                  ? 'bg-violet-600 border-violet-600 text-white'
                  : 'bg-white border-zinc-300 text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/invoices/customers"
            className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-violet-300 text-[11px] font-bold uppercase tracking-wider text-violet-700 hover:bg-violet-50"
            data-testid="ar-customers-workbench"
          >
            All customers →
          </Link>
          <a
            href={arEffectivenessCsvUrl(windowDays)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider hover:bg-zinc-50"
            data-testid="ar-export-csv"
          >
            <FileCsv size={12} weight="bold" /> CSV
          </a>
          <button
            onClick={() => setExpanded((x) => !x)}
            className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 text-[11px] font-bold uppercase tracking-wider hover:bg-zinc-50"
            data-testid="ar-toggle-details"
          >
            {expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
            {expanded ? 'Hide' : 'Details'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile
          testid="ar-tile-reminders"
          icon={EnvelopeSimple}
          label="Reminders sent"
          value={sent.total || 0}
          accent="violet"
          sub={
            <span className="flex items-center gap-2">
              <span className={`inline-block w-1.5 h-1.5 ${TONE_DOT.friendly}`} />{sent.friendly || 0}
              <span className={`inline-block w-1.5 h-1.5 ${TONE_DOT.firm}`} />{sent.firm || 0}
              <span className={`inline-block w-1.5 h-1.5 ${TONE_DOT.final}`} />{sent.final || 0}
            </span>
          }
        />
        <Tile
          testid="ar-tile-payrate"
          icon={Lightning}
          label="Pay-rate ≤ 7d"
          value={pct(overallPay)}
          accent="emerald"
          sub={`F ${pct(pr.friendly)} · Fm ${pct(pr.firm)} · Fn ${pct(pr.final)}`}
        />
        <Tile
          testid="ar-tile-avg-dtp"
          icon={Clock}
          label="Avg days to pay"
          value={dtp.overall != null ? `${dtp.overall}d` : '—'}
          accent="sky"
          sub={
            dtp.friendly != null || dtp.firm != null || dtp.final != null
              ? `F ${dtp.friendly ?? '—'}d · Fm ${dtp.firm ?? '—'}d · Fn ${dtp.final ?? '—'}d`
              : 'No payments yet in window'
          }
        />
        <Tile
          testid="ar-tile-recovered"
          icon={CurrencyDollar}
          label="Recovered ≤ 14d"
          value={formatCurrency(rec.amount, rec.currency)}
          accent="amber"
          sub={`${rec.count} invoice${rec.count === 1 ? '' : 's'}`}
        />
      </div>

      {top.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="ar-top-chips">
          <Trophy size={12} weight="bold" className="text-amber-600 shrink-0" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Top responsive:</span>
          {top.slice(0, 3).map((c, i) => {
            const inner = (
              <>
                <span className="font-bold truncate max-w-[8rem]">{c.customer_name}</span>
                <span className={`px-1 py-0.5 text-[9px] font-bold border ${
                  c.pay_rate >= 0.5 ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : c.pay_rate > 0 ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-zinc-50 border-zinc-300 text-zinc-500'
                }`}>{pct(c.pay_rate)}</span>
              </>
            );
            return c.customer_email ? (
              <Link
                key={i}
                to={`/invoices/customer/${encodeURIComponent(c.customer_email)}`}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-zinc-300 text-[11px] hover:border-violet-400 hover:bg-violet-50"
                data-testid={`ar-top-chip-${i}`}
              >
                {inner}
              </Link>
            ) : (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-zinc-300 text-[11px]" data-testid={`ar-top-chip-${i}`}>
                {inner}
              </span>
            );
          })}
        </div>
      )}

      {expanded && (
        <div className="bg-white border border-zinc-200 p-4" data-testid="ar-details-panel">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={14} weight="bold" className="text-amber-600" />
            <h3 className="text-xs font-bold uppercase tracking-widest">Top responsive customers</h3>
          </div>
          {top.length === 0 ? (
            <div className="text-xs text-zinc-400 italic">No customers reminded in this window yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b">
                <tr>
                  <th className="py-1.5 text-left">Customer</th>
                  <th className="py-1.5 text-right">Reminders</th>
                  <th className="py-1.5 text-right">Paid</th>
                  <th className="py-1.5 text-right">Pay-rate</th>
                  <th className="py-1.5 text-right">Recovered</th>
                </tr>
              </thead>
              <tbody>
                {top.map((c, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="py-1.5 truncate max-w-[14rem]" data-testid={`ar-top-customer-${i}`}>
                      {c.customer_email ? (
                        <Link
                          to={`/invoices/customer/${encodeURIComponent(c.customer_email)}`}
                          className="hover:text-violet-700 hover:underline"
                          data-testid={`ar-top-customer-link-${i}`}
                        >
                          {c.customer_name}
                        </Link>
                      ) : c.customer_name}
                    </td>
                    <td className="py-1.5 text-right font-mono">{c.reminders_count}</td>
                    <td className="py-1.5 text-right font-mono">{c.paid_count}</td>
                    <td className="py-1.5 text-right font-mono">
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold border ${
                        c.pay_rate >= 0.5 ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                          : c.pay_rate > 0 ? 'bg-amber-50 border-amber-300 text-amber-700'
                          : 'bg-zinc-50 border-zinc-300 text-zinc-500'
                      }`}>{pct(c.pay_rate)}</span>
                    </td>
                    <td className="py-1.5 text-right font-mono font-bold">{formatCurrency(c.total_recovered, c.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
